import { useEffect } from 'react';

interface SchemaOrgProps {
  type: 'LocalBusiness' | 'WebPage' | 'FAQPage' | 'Service';
  data: Record<string, any>;
}

export const SchemaOrg: React.FC<SchemaOrgProps> = ({ type, data }) => {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = `schema-${type.toLowerCase()}`;
    
    let schemaData: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': type,
      ...data
    };

    script.text = JSON.stringify(schemaData);
    
    // Remove existing schema of same type
    const existing = document.getElementById(`schema-${type.toLowerCase()}`);
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById(`schema-${type.toLowerCase()}`);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [type, data]);

  return null;
};

export const getLocalBusinessSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    name: 'ぽかぽか堂 よりみち相談室',
    image: `${siteUrl}/vite.svg`,
    '@id': siteUrl,
    url: siteUrl,
    telephone: '',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '',
      addressLocality: '松伏町',
      addressRegion: '埼玉県',
      postalCode: '',
      addressCountry: 'JP'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 35.9167,
      longitude: 139.8000
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00'
      }
    ],
    sameAs: [],
    priceRange: '¥¥',
    areaServed: {
      '@type': 'City',
      name: '松伏町'
    },
    description: 'PC・Web操作が苦手な地元事業者様へ、SNS・Canva・公式LINE・ネットショップなどの実務サポートを丁寧に提供。越谷・松伏エリアの中小企業・個人店向け伴走型サポートサービス。'
  };
};

export const getWebPageSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@id': `${siteUrl}/#webpage`,
    url: siteUrl,
    name: '松伏町 Web操作サポート ぽかぽか堂 よりみち相談室',
    isPartOf: {
      '@id': `${siteUrl}/#website`
    },
    about: {
      '@id': `${siteUrl}/#organization`
    },
    description: 'PC・Web操作が苦手な地元事業者様へ、SNS・Canva・公式LINE・ネットショップなどの実務サポートを丁寧に提供。',
    inLanguage: 'ja'
  };
};

export const getFAQSchema = () => {
  return {
    mainEntity: [
      {
        '@type': 'Question',
        name: 'どのようなサポートを提供していますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'SNS運用、Canvaでのデザイン作成、公式LINE設定、ネットショップ構築など、Web操作全般のサポートを提供しています。'
        }
      },
      {
        '@type': 'Question',
        name: 'サポート対象エリアはどこですか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '埼玉県北葛飾郡松伏町を中心に、越谷エリアの事業者様をサポートしています。'
        }
      },
      {
        '@type': 'Question',
        name: 'LINE質問し放題とは何ですか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '月曜日から金曜日まで、LINEチャットで何度でもご質問いただけるサービスです。Web操作に関する疑問をいつでもお気軽にご相談ください。'
        }
      }
    ]
  };
};

export const getServiceSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@type': 'Service',
    serviceType: 'Web操作サポート',
    provider: {
      '@type': 'LocalBusiness',
      name: 'ぽかぽか堂 よりみち相談室'
    },
    areaServed: {
      '@type': 'City',
      name: '松伏町'
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'サポートサービス',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'SNS運用サポート',
            description: 'Instagram、Facebook、X(Twitter)などのSNS運用をサポート'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Canvaデザインサポート',
            description: 'Canvaを使った画像・チラシ・SNS投稿のデザイン作成サポート'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '公式LINE設定サポート',
            description: '公式LINEアカウントの開設から運用までをサポート'
          }
        }
      ]
    },
    url: siteUrl
  };
};
